import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import { AddUserDialog } from "./AddUserDialog";
import { EditUserDialog } from "./EditUserDialog";
import { PasswordResetDialog } from "./PasswordResetDialog";

const ROLE_OPTIONS = [
  { value: "buyer", label: "Buyer" },
  { value: "artist", label: "Artist" },
  { value: "art_center", label: "Art Hub" },
  { value: "journalist", label: "Journalist" },
  { value: "storyteller", label: "Storyteller" },
  { value: "writer", label: "Writer" },
  { value: "admin", label: "Admin" },
];

const APPROVAL_TABS = [
  { key: "all", label: "All" },
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
];

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [searchParams, setSearchParams] = useSearchParams();
  const approvalFilter = searchParams.get("approval") || "all";
  const [addOpen, setAddOpen] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [editUser, setEditUser] = useState(null);

  const load = async () => {
    try {
      const params = { limit: 200 };
      if (approvalFilter !== "all") params.approval = approvalFilter;
      if (showDeleted) params.include_deleted = true;
      const { data } = await api.get("/admin/users", { params });
      setUsers(data.items || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load users");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approvalFilter, showDeleted]);

  const removeUser = async (id, name) => {
    if (!window.confirm(`Remove ${name}? They will no longer be able to log in. Their artworks and orders are preserved.`)) return;
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success("User removed");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Remove failed");
    }
  };

  const restoreUser = async (id) => {
    try {
      await api.post(`/admin/users/${id}/restore`);
      toast.success("User restored · can log in again");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Restore failed");
    }
  };

  const updateUser = async (id, patch) => {
    try {
      const { data } = await api.patch(`/admin/users/${id}`, patch);
      setUsers((u) => u.map((x) => (x.id === id ? { ...x, ...data } : x)));
      toast.success("Updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed");
    }
  };

  const approveUser = async (id) => {
    try {
      const { data } = await api.post(`/admin/users/${id}/approve`);
      setUsers((u) => u.map((x) => (x.id === id ? { ...x, ...data } : x)));
      toast.success("Account approved · email sent");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Approve failed");
    }
  };

  const rejectUser = async (id) => {
    if (!window.confirm("Reject this account? They'll be unable to use the platform.")) return;
    try {
      const { data } = await api.post(`/admin/users/${id}/reject`);
      setUsers((u) => u.map((x) => (x.id === id ? { ...x, ...data } : x)));
      toast.success("Account rejected · email sent");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Reject failed");
    }
  };

  const publishArtist = async (id) => {
    try {
      const { data } = await api.post(`/admin/users/${id}/publish`);
      setUsers((u) => u.map((x) => (x.id === id ? { ...x, ...data } : x)));
      toast.success("Artist published · visible on public site");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Publish failed");
    }
  };

  const unpublishArtist = async (id, name) => {
    if (!window.confirm(`Unpublish ${name}? Their profile will be hidden from the public site (account remains active).`)) return;
    try {
      const { data } = await api.post(`/admin/users/${id}/unpublish`);
      setUsers((u) => u.map((x) => (x.id === id ? { ...x, ...data } : x)));
      toast.success("Artist unpublished · hidden from public site");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Unpublish failed");
    }
  };

  const setApprovalTab = (key) => {
    if (key === "all") setSearchParams({});
    else setSearchParams({ approval: key });
  };

  const filtered = users.filter(
    (u) =>
      (roleFilter === "all" || u.role === roleFilter) &&
      (u.name.toLowerCase().includes(search.toLowerCase()) ||
        u.email.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div data-testid="admin-users">
      <p className="label-eyebrow text-[#1E3A8A]">— Members</p>
      <h1 className="font-serif-display text-5xl tracking-tighter font-medium mt-3 text-[#111]">
        Users
      </h1>
      <p className="text-[#555] mt-3">
        {users.length} accounts · approve, suspend, promote, or audit any user.
      </p>

      <div className="mt-8 flex items-center justify-between gap-4 border-b border-[#E5E0D8] flex-wrap">
        <div className="flex gap-2 flex-wrap">
          {APPROVAL_TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`approval-tab-${t.key}`}
              onClick={() => setApprovalTab(t.key)}
              className={`label-eyebrow px-5 py-3 -mb-px border-b-2 transition-colors ${
                approvalFilter === t.key
                  ? "border-[#1E3A8A] text-[#1E3A8A]"
                  : "border-transparent text-[#555] hover:text-[#111]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <label className="label-eyebrow text-[#555] flex items-center gap-2 cursor-pointer pb-3">
          <input
            type="checkbox"
            checked={showDeleted}
            onChange={(e) => setShowDeleted(e.target.checked)}
            data-testid="show-deleted-toggle"
            className="accent-[#1E3A8A]"
          />
          Show removed
        </label>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Input
          data-testid="users-search"
          placeholder="Search by name or email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="rounded-none border-b-2 border-l-0 border-r-0 border-t-0 border-[#E5E0D8] bg-transparent px-0 focus-visible:ring-0 focus-visible:border-[#1E3A8A]"
        />
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger
            data-testid="users-role-filter"
            className="rounded-none border-b-2 border-l-0 border-r-0 border-t-0 border-[#E5E0D8] bg-transparent px-0 focus:ring-0"
          >
            <SelectValue placeholder="Role" />
          </SelectTrigger>
          <SelectContent className="rounded-none">
            <SelectItem value="all">All roles</SelectItem>
            {ROLE_OPTIONS.map((r) => (
              <SelectItem key={r.value} value={r.value}>
                {r.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="border border-[#E5E0D8] bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F4EFE6]/40 border-b border-[#E5E0D8]">
            <tr>
              <th className="text-left p-3 label-eyebrow text-[#555]">Name</th>
              <th className="text-left p-3 label-eyebrow text-[#555]">Email</th>
              <th className="text-left p-3 label-eyebrow text-[#555]">Role</th>
              <th className="text-left p-3 label-eyebrow text-[#555]">Country</th>
              <th className="text-left p-3 label-eyebrow text-[#555]">Joined</th>
              <th className="text-right p-3 label-eyebrow text-[#555]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((u) => (
              <tr
                key={u.id}
                data-testid={`user-row-${u.id}`}
                className="border-b border-[#E5E0D8] last:border-b-0 hover:bg-[#F4EFE6]/30"
              >
                <td className="p-3 font-serif-display text-base">
                  <button
                    type="button"
                    onClick={() => setEditUser(u)}
                    data-testid={`edit-user-name-${u.id}`}
                    className="text-left hover:text-[#1E3A8A] hover:underline decoration-[#F4C430] underline-offset-4"
                    title="Edit user"
                  >
                    {u.name}
                  </button>
                  {u.suspended && <span className="ml-2 label-eyebrow text-[#D32F2F]">SUSPENDED</span>}
                  {u.deleted_at && (
                    <span className="ml-2 label-eyebrow bg-[#D32F2F] text-white px-2 py-0.5">REMOVED</span>
                  )}
                  {u.approval_status === "pending" && (
                    <span className="ml-2 label-eyebrow bg-[#F4C430]/30 text-[#1E3A8A] px-2 py-0.5">PENDING</span>
                  )}
                  {u.approval_status === "rejected" && !u.deleted_at && (
                    <span className="ml-2 label-eyebrow bg-[#D32F2F] text-white px-2 py-0.5">REJECTED</span>
                  )}
                  {u.role === "artist" && u.publish_status === "draft" && !u.deleted_at && (
                    <span
                      data-testid={`artist-draft-badge-${u.id}`}
                      className="ml-2 label-eyebrow bg-[#1E3A8A]/10 text-[#1E3A8A] px-2 py-0.5 border border-[#1E3A8A]/30"
                    >
                      DRAFT
                    </span>
                  )}
                </td>
                <td className="p-3 text-[#333]">{u.email}</td>
                <td className="p-3">
                  <Select
                    value={u.role}
                    onValueChange={(newRole) => {
                      if (newRole === u.role) return;
                      if (
                        !window.confirm(
                          `Change ${u.name}'s role from ${u.role} to ${newRole}? ` +
                          (newRole === "admin"
                            ? "This grants full admin permissions."
                            : u.role === "admin"
                              ? "This will REMOVE their admin privileges."
                              : "Their account stays approved.")
                        )
                      ) {
                        return;
                      }
                      updateUser(u.id, { role: newRole });
                    }}
                  >
                    <SelectTrigger
                      data-testid={`role-select-${u.id}`}
                      className="h-7 w-[140px] rounded-none border border-[#1E3A8A] bg-[#1E3A8A] text-[#F4C430] px-2 py-0 label-eyebrow focus:ring-0 focus:ring-offset-0 [&>svg]:text-[#F4C430]"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      {ROLE_OPTIONS.map((r) => (
                        <SelectItem
                          key={r.value}
                          value={r.value}
                          data-testid={`role-option-${u.id}-${r.value}`}
                        >
                          {r.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
                <td className="p-3 text-[#555]">{u.country || "—"}</td>
                <td className="p-3 font-mono-aam text-xs text-[#555]">
                  {u.created_at?.slice(0, 10)}
                </td>
                <td className="p-3 text-right">
                  {u.deleted_at ? (
                    <button
                      onClick={() => restoreUser(u.id)}
                      data-testid={`restore-user-${u.id}`}
                      className="label-eyebrow text-[#1E3A8A] hover:text-[#111]"
                    >
                      Restore
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={() => setEditUser(u)}
                        data-testid={`edit-user-${u.id}`}
                        className="label-eyebrow text-[#1E3A8A] hover:text-[#111] mr-3"
                      >
                        Edit
                      </button>
                      {u.approval_status === "pending" && u.role !== "admin" && (
                        <>
                          <button
                            onClick={() => approveUser(u.id)}
                            data-testid={`approve-user-${u.id}`}
                            className="label-eyebrow text-[#1E3A8A] hover:text-[#111] mr-3"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => rejectUser(u.id)}
                            data-testid={`reject-user-${u.id}`}
                            className="label-eyebrow text-[#D32F2F] hover:underline mr-3"
                          >
                            Reject
                          </button>
                        </>
                      )}
                      {u.approval_status === "rejected" && u.role !== "admin" && (
                        <button
                          onClick={() => approveUser(u.id)}
                          data-testid={`reapprove-user-${u.id}`}
                          className="label-eyebrow text-[#1E3A8A] hover:text-[#111] mr-3"
                        >
                          Reinstate
                        </button>
                      )}
                      {u.role !== "admin" && u.approval_status === "approved" && (
                        <button
                          onClick={() => updateUser(u.id, { suspended: !u.suspended })}
                          data-testid={`suspend-user-${u.id}`}
                          className="label-eyebrow text-[#1E3A8A] hover:text-[#D32F2F] mr-3"
                        >
                          {u.suspended ? "Reinstate" : "Suspend"}
                        </button>
                      )}
                      {u.role === "artist" && u.approval_status === "approved" && !u.suspended && (
                        u.publish_status === "draft" ? (
                          <button
                            onClick={() => publishArtist(u.id)}
                            data-testid={`publish-artist-${u.id}`}
                            className="label-eyebrow text-[#1E3A8A] hover:text-[#111] mr-3"
                          >
                            Publish
                          </button>
                        ) : (
                          <button
                            onClick={() => unpublishArtist(u.id, u.name)}
                            data-testid={`unpublish-artist-${u.id}`}
                            className="label-eyebrow text-[#555] hover:text-[#D32F2F] mr-3"
                          >
                            Unpublish
                          </button>
                        )
                      )}
                      {u.role === "buyer" && u.approval_status === "approved" && (
                        <button
                          onClick={() => updateUser(u.id, { role: "artist" })}
                          data-testid={`promote-user-${u.id}`}
                          className="label-eyebrow text-[#1E3A8A] hover:text-[#111] mr-3"
                          title="Shortcut — same effect as picking 'Artist' from the role dropdown"
                        >
                          Make artist
                        </button>
                      )}
                      {u.role !== "admin" && (
                        <button
                          onClick={() => setResetUser(u)}
                          data-testid={`reset-password-user-${u.id}`}
                          className="label-eyebrow text-[#1E3A8A] hover:text-[#111] mr-3"
                        >
                          Reset pw
                        </button>
                      )}
                      {u.role !== "admin" && (
                        <button
                          onClick={() => removeUser(u.id, u.name)}
                          data-testid={`remove-user-${u.id}`}
                          className="label-eyebrow text-[#D32F2F] hover:underline"
                        >
                          Remove
                        </button>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={6} className="p-8 text-center text-[#555]">
                  No users match.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <PasswordResetDialog
        user={resetUser}
        open={!!resetUser}
        onClose={() => setResetUser(null)}
      />
      <EditUserDialog
        user={editUser}
        open={!!editUser}
        onClose={() => setEditUser(null)}
        onUpdated={(updated) =>
          setUsers((u) => u.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)))
        }
      />
    </div>
  );
}
